import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { BehaviorSubject, catchError, filter, Observable, take, tap, throwError, switchMap } from 'rxjs';
import { Router } from '@angular/router';
import { UserService } from '@shared/modules/account/services/user.service';
import { environment } from '@env';
import { ModalService } from '@shared/services/modal.service';

@Injectable()
export class AuthResponseInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private router: Router, private userService: UserService, private modalService: ModalService) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.url.endsWith('/auth/refresh')) {
      return next.handle(req);
    }
    
    if (!req.url.includes(environment.inCloud.baseUrl) || !req.headers.has('Authorization')) {
      return next.handle(req).pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        }));
    }

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        var newHttpErrorResponse: HttpErrorResponse;

        switch (error.status) {
          case 401:
            if (error.error?.code === 'SESSION_REPLACED') {
              this.refreshTokenInProgress = false;
              this.refreshTokenSubject.next(null);
            
              this.userService.logoutLocally();

              this.modalService.showSessionReplacedModal();
              return throwError(() => error);
            }

            if (this.refreshTokenInProgress) {
              return this.refreshTokenSubject.pipe(
                filter(result => result !== null),
                take(1),
                switchMap(accessToken => {
                  return next.handle(this.addToken(req, accessToken));
                }));
            } else {
              this.refreshTokenInProgress = true;
              this.refreshTokenSubject.next(null);

              return this.userService.refreshAccess().pipe(
                switchMap((user: any) => {
                  this.refreshTokenInProgress = false;
                  this.refreshTokenSubject.next(user.accessToken);
              
                  return next.handle(
                    this.addToken(req, user.accessToken)
                  );
                }),
                catchError((err) => {
                  this.refreshTokenInProgress = false;
                  this.refreshTokenSubject.next(null);
              
                  this.userService.logoutLocally();
              
                  return throwError(() => err);
                })
              );
            }
          case 400:
          case 997:
          case 404:
            if (/auth\/refresh$/.test(req.url)) {
              this.userService.logoutLocally();
            }

            return throwError(() => error);
          case 403:
            newHttpErrorResponse = new HttpErrorResponse({
              error: 403,
            });
            
            this.router.navigateByUrl('/dashboard');
            
            return throwError(() => newHttpErrorResponse);
          case 500:
            newHttpErrorResponse = new HttpErrorResponse({
              error: 4100
            });

            return throwError(() => newHttpErrorResponse);
          case 441:
            this.userService.logoutLocally();

            newHttpErrorResponse = new HttpErrorResponse({
              error: 403
            });

            return throwError(() => newHttpErrorResponse);
          default:
            return throwError(() => error);
        }
      })
    );
  }

  private addToken(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
